import { useEffect } from "react";

type ArtworkDocumentMeta = {
  title: string;
  description: string;
  imageUrl: string;
  canonicalPath: string;
};

function upsertMeta(
  selector: string,
  createAttrs: Record<string, string>,
  content: string,
) {
  let el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    for (const [key, value] of Object.entries(createAttrs)) {
      el.setAttribute(key, value);
    }
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** Client-side head tags for artwork detail pages (humans + in-app navigation). */
export function useArtworkDocumentMeta(meta: ArtworkDocumentMeta | null) {
  useEffect(() => {
    if (!meta) return;

    const canonical = `${window.location.origin}${meta.canonicalPath}`;
    document.title = meta.title;

    upsertMeta(
      'meta[name="description"]',
      { name: "description" },
      meta.description,
    );
    upsertMeta('meta[property="og:title"]', { property: "og:title" }, meta.title);
    upsertMeta(
      'meta[property="og:description"]',
      { property: "og:description" },
      meta.description,
    );
    upsertMeta('meta[property="og:image"]', { property: "og:image" }, meta.imageUrl);
    upsertMeta('meta[property="og:url"]', { property: "og:url" }, canonical);
    upsertMeta(
      'meta[name="twitter:card"]',
      { name: "twitter:card" },
      "summary_large_image",
    );
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image" }, meta.imageUrl);

    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonical;
  }, [meta]);
}
